import Button from '@mui/material/Button';
import React, { memo } from 'react';
import { FormattedMessage } from 'react-intl';

import { neutral, petrol, yellow } from '../../../styles/mui/colors';
import IconList from '../../Icons/IconList';
import IconMap from '../../Icons/IconMap';

const toMapButtonStyle = (theme) => ({
  borderRadius: 200,
  display: 'flex',
  position: 'fixed',
  bottom: '120px',
  right: '16px',
  zIndex: theme.globals.zIndexToggleMapMobileButton,
});

/**
 * ToggleMapButtonMobileComponent is a button that toggles between showing a map or a list view on mobile devices.
 * Depending on the `shouldShowSearchMap` prop, it displays either a button to go back to the listings or to open the map view.
 *
 * @component
 *
 * @param {object} props - The component props.
 * @param {Function} props.onListIconClick - Callback function to handle the click event when switching to the list view.
 * @param {Function} props.onMapIconClick - Callback function to handle the click event when switching to the map view.
 * @param {boolean} props.shouldShowSearchMap - Determines whether the map view is currently shown or not.
 *
 * @returns {JSX.Element} A button that toggles between map and list views.
 */
const ToggleMapButtonMobileComponent = ({
  onListIconClick,
  onMapIconClick,
  shouldShowSearchMap = false,
}) => {
  return shouldShowSearchMap ? (
    <Button
      autoFocus={false}
      onClick={onListIconClick}
      startIcon={<IconList color={petrol.main} />}
      sx={(theme) => ({
        ...toMapButtonStyle(theme),
        borderRadius: 200,
        bgcolor: 'white',
        borderColor: neutral[200],
        '&:hover': {
          bgcolor: 'white',
        },
        '&:active': {
          bgcolor: 'white',
        },
        '&:focus': {
          bgcolor: 'white',
        },
        '& .MuiButton-startIcon': {
          color: 'inherit',
        },
        color: 'inherit',
      })}
      variant="outlined"
    >
      <FormattedMessage id="SearchPage.backToListingsButton" />
    </Button>
  ) : (
    <Button
      autoFocus={false}
      onClick={onMapIconClick}
      startIcon={<IconMap />}
      sx={(theme) => ({
        ...toMapButtonStyle(theme),
        '&:hover': {
          backgroundColor: yellow.main,
          color: petrol.main,
        },
      })}
      variant="contained"
    >
      <FormattedMessage id="SearchFiltersMobile.openMapView" />
    </Button>
  );
};

ToggleMapButtonMobileComponent.displayName = 'ToggleMapButtonMobile';
const ToggleMapButtonMobile = memo(ToggleMapButtonMobileComponent);
export default ToggleMapButtonMobile;
